import React, { useState } from 'react';

// ─── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 520 }) {
  if (!open) return null;
  return (
    <div style={{ position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
      <div onClick={onClose} style={{ position:'absolute',inset:0,background:'rgba(0,0,0,0.7)',backdropFilter:'blur(4px)' }} />
      <div style={{
        position:'relative',zIndex:1,background:'#1e1e2e',
        border:'1px solid rgba(255,255,255,0.1)',borderRadius:20,
        width:'100%',maxWidth:width,padding:'32px 28px',
        maxHeight:'90vh',overflowY:'auto',
        animation:'modalIn 0.22s cubic-bezier(0.16,1,0.3,1)',
      }}>
        <style>{`@keyframes modalIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}`}</style>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24 }}>
          <h2 style={{ color:'#fff',fontSize:19,fontWeight:700,margin:0 }}>{title}</h2>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.08)',border:'none',borderRadius:8,width:32,height:32,color:'rgba(255,255,255,0.6)',cursor:'pointer',fontSize:20,lineHeight:1,display:'flex',alignItems:'center',justifyContent:'center' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Field ────────────────────────────────────────────────────────────────────
export function Field({ label, error, children, style }) {
  return (
    <div style={{ marginBottom:16,...style }}>
      {label && <label style={{ display:'block',color:'rgba(255,255,255,0.55)',fontSize:13,fontWeight:500,marginBottom:6 }}>{label}</label>}
      {children}
      {error && <span style={{ color:'#fca5a5',fontSize:12,marginTop:4,display:'block' }}>{error}</span>}
    </div>
  );
}

const inputStyle = {
  width:'100%',padding:'11px 14px',
  background:'rgba(255,255,255,0.06)',
  border:'1px solid rgba(255,255,255,0.1)',
  borderRadius:10,color:'#fff',fontSize:14,
  fontFamily:'inherit',outline:'none',transition:'all 0.2s',
};

export function Input({ label, error, wrapStyle, ...props }) {
  return (
    <Field label={label} error={error} style={wrapStyle}>
      <input style={inputStyle}
        onFocus={e => { e.target.style.borderColor='rgba(124,58,237,0.6)'; e.target.style.background='rgba(255,255,255,0.09)'; }}
        onBlur={e => { e.target.style.borderColor='rgba(255,255,255,0.1)'; e.target.style.background='rgba(255,255,255,0.06)'; }}
        {...props}
      />
    </Field>
  );
}

export function Textarea({ label, error, wrapStyle, ...props }) {
  return (
    <Field label={label} error={error} style={wrapStyle}>
      <textarea style={{ ...inputStyle,minHeight:90,resize:'vertical' }}
        onFocus={e => { e.target.style.borderColor='rgba(124,58,237,0.6)'; }}
        onBlur={e => { e.target.style.borderColor='rgba(255,255,255,0.1)'; }}
        {...props}
      />
    </Field>
  );
}

// ─── Button ───────────────────────────────────────────────────────────────────
const btnVariants = {
  primary: { background:'linear-gradient(135deg,#7c3aed,#4f46e5)',color:'#fff',border:'none' },
  danger:  { background:'rgba(239,68,68,0.18)',border:'1px solid rgba(239,68,68,0.35)',color:'#fca5a5' },
  ghost:   { background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.12)',color:'rgba(255,255,255,0.75)' },
  success: { background:'rgba(34,197,94,0.18)',border:'1px solid rgba(34,197,94,0.35)',color:'#86efac' },
  warning: { background:'rgba(234,179,8,0.18)',border:'1px solid rgba(234,179,8,0.35)',color:'#fde047' },
};

export function Btn({ children, variant='primary', size='md', loading, style, ...props }) {
  const pad = size === 'sm' ? '7px 14px' : size === 'lg' ? '14px 28px' : '11px 20px';
  const fz  = size === 'sm' ? 13 : size === 'lg' ? 16 : 14;
  return (
    <button
      style={{
        padding:pad, fontSize:fz, borderRadius:10,
        fontFamily:'inherit', fontWeight:600, cursor:'pointer',
        transition:'all 0.16s', display:'inline-flex',
        alignItems:'center', gap:6, whiteSpace:'nowrap',
        ...(loading && { opacity:0.6, cursor:'not-allowed' }),
        ...btnVariants[variant],
        ...style,
      }}
      disabled={!!loading}
      {...props}
    >
      {loading ? '...' : children}
    </button>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────
const badgeColors = {
  green:  { bg:'rgba(34,197,94,0.15)',  border:'rgba(34,197,94,0.3)',  text:'#86efac' },
  red:    { bg:'rgba(239,68,68,0.15)',  border:'rgba(239,68,68,0.3)',  text:'#fca5a5' },
  yellow: { bg:'rgba(234,179,8,0.15)',  border:'rgba(234,179,8,0.3)',  text:'#fde047' },
  purple: { bg:'rgba(124,58,237,0.15)', border:'rgba(124,58,237,0.3)', text:'#c4b5fd' },
  blue:   { bg:'rgba(59,130,246,0.15)', border:'rgba(59,130,246,0.3)', text:'#93c5fd' },
};

export function Badge({ children, color='green' }) {
  const c = badgeColors[color] || badgeColors.green;
  return (
    <span style={{
      display:'inline-flex',alignItems:'center',padding:'3px 10px',
      borderRadius:20,fontSize:12,fontWeight:600,
      background:c.bg,border:`1px solid ${c.border}`,color:c.text,
    }}>{children}</span>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
let _setToasts = null;

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);
  _setToasts = setToasts;
  return (
    <div style={{ position:'fixed',bottom:24,right:24,zIndex:9999,display:'flex',flexDirection:'column',gap:8,pointerEvents:'none' }}>
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateX(24px)}to{opacity:1;transform:translateX(0)}}`}</style>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding:'12px 18px',borderRadius:12,fontSize:14,fontWeight:500,
          background: t.type === 'error' ? 'rgba(220,38,38,0.97)' : 'rgba(22,163,74,0.97)',
          color:'#fff',boxShadow:'0 8px 24px rgba(0,0,0,0.3)',
          animation:'toastIn 0.28s cubic-bezier(0.16,1,0.3,1)',
        }}>{t.msg}</div>
      ))}
    </div>
  );
}

export function toast(msg, type='success') {
  if (!_setToasts) return;
  const id = Date.now() + Math.random();
  _setToasts(p => [...p, { id, msg, type }]);
  setTimeout(() => _setToasts(p => p.filter(t => t.id !== id)), 3200);
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────
export function useConfirm() {
  const [state, setState] = useState({ open:false, msg:'', resolve:null });

  const confirm = (msg) => new Promise(resolve => setState({ open:true, msg, resolve }));
  const respond = (val) => { state.resolve?.(val); setState({ open:false, msg:'', resolve:null }); };

  const Dialog = () => !state.open ? null : (
    <div style={{ position:'fixed',inset:0,zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
      <div onClick={() => respond(false)} style={{ position:'absolute',inset:0,background:'rgba(0,0,0,0.7)' }} />
      <div style={{ position:'relative',zIndex:1,background:'#1e1e2e',border:'1px solid rgba(255,255,255,0.1)',borderRadius:16,padding:'28px 24px',width:'100%',maxWidth:360,textAlign:'center' }}>
        <div style={{ fontSize:44,marginBottom:12 }}>⚠️</div>
        <p style={{ color:'rgba(255,255,255,0.85)',fontSize:15,marginBottom:24,lineHeight:1.55 }}>{state.msg}</p>
        <div style={{ display:'flex',gap:10,justifyContent:'center' }}>
          <Btn variant="ghost" onClick={() => respond(false)}>Cancelar</Btn>
          <Btn variant="danger" onClick={() => respond(true)}>Confirmar</Btn>
        </div>
      </div>
    </div>
  );

  return { confirm, Dialog };
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ size=40, color='#7c3aed' }) {
  return (
    <>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width:size,height:size,border:`3px solid ${color}33`,borderTopColor:color,borderRadius:'50%',animation:'spin 0.75s linear infinite' }} />
    </>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────
export function Card({ children, style }) {
  return (
    <div style={{
      background:'rgba(255,255,255,0.04)',
      border:'1px solid rgba(255,255,255,0.08)',
      borderRadius:16,padding:24,...style,
    }}>{children}</div>
  );
}
